import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, Typography, CircularProgress } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { useParams } from 'react-router-dom'
import { useModule } from '../../context/ModuleContext/ModuleContext'
import { useQeustion, useQuestionDispatch } from '../../context/QuestionContext/QuestionContext'
import { useCountdown } from '../../hooks/useCountdown'
import { useGetUser } from '../../hooks/useGetUser'
import { ADD_CURRENT_QUESTION, SET_CURRENT_QUESTION } from '../../reducers/reducesTypes'
import { BaseService } from '../../services/BaseService'
import { removeLocalStorageData } from '../../utils/storage/storage'
import { createQuestionValidation } from '../../validations/questionValidations'
import Modal from '../Modal'
import QuestionHeader from './QuestionHeader'
import { getCurrentPosition } from '../../utils/geolocation'
import { calculateSessionTime } from '../../utils/calculateSessionTime'
import { useLogging } from '../../context/LoggingContext/LoggingContext'
import { buildCameraTargets } from '../../utils/buildCameraTargets'
import { shouldCaptureCamera } from '../../utils/shouldCaptureCamera'
import { useCameraContext } from '../../context/CameraContext/CameraContext'

async function post(payload, rules) {
  const { time_start, time_end, durasi } = calculateSessionTime(
    JSON.parse(localStorage.getItem('question')),
  )

  let location = null

  if (rules?.geo !== 0) {
    try {
      location = await getCurrentPosition({ timeout: 15000 })
    } catch (err) {
      const cached = localStorage.getItem('geoLoc')
      if (cached) {
        location = JSON.parse(cached)
      } else {
        console.warn('Gagal ambil lokasi:', err.message)
      }
    }
  }

  const finalPayload = {
    ...payload,
    time_Start: time_start,
    time_End: time_end,
    durasi,
    latitude: location?.lat || null,
    longitude: location?.lon || null,
  }

  const service = new BaseService('/m_activity_2')
  const res = await service.postAnswer(finalPayload)
  return res.data
}

const Wizard = React.memo(({ children, onTestEnded }) => {
  const { id } = useParams()
  // get question from local storage
  const questionStorage = useMemo(() => {
    const dataStore = localStorage.getItem('question')
      ? JSON.parse(localStorage.getItem('question'))
      : null

    if (Number(dataStore?.questionID) === Number(id)) {
      return dataStore
    } else {
      return null
    }
  }, [id])

  const { modules } = useModule()
  const maxCount = React.Children.toArray(children).length - 1
  const minCount = questionStorage?.currentPage ?? 0
  const [current, setCurrent] = useState(minCount)
  const dataQuestion = useQeustion()
  const [minutes, seconds, isStop] = useCountdown({
    initialMinute: questionStorage?.minutes ?? Number(modules.waktu_pengerjaan),
    initialSeconds: questionStorage?.seconds ?? 0,
  })
  const { addLog } = useLogging()
  const totalQuestion = React.Children.toArray(children).length
  const { captureOnce } = useCameraContext()

  const dispatch = useQuestionDispatch()
  const { user } = useGetUser()
  const [isOpen, setIsOpen] = useState(false)

  const isSubmittingRef = useRef(false)
  const btnSubmitRef = useRef(null)

  const [modalContent, setModalContent] = useState()
  const form = useForm({
    ...(questionStorage && { defaultValues: questionStorage?.questions || {} }),
    resolver: yupResolver(
      createQuestionValidation(
        dataQuestion.moduleType,
        current + 1,
        {
          timer: {
            minutes,
            seconds,
          },
        },
        {
          maxNumber: dataQuestion.questionsList[current]?.esai_length,
          question: dataQuestion.questionsList[current],
        },
      ),
    ),
  })

  const rules = useMemo(() => {
    try {
      return typeof modules?.rules === 'string' ? JSON.parse(modules.rules) : modules?.rules || {}
    } catch {
      return {}
    }
  }, [modules?.rules])

  const { mutate, isLoading, isError, isSuccess } = useMutation((payload) => post(payload, rules), {
    onSuccess: async (res) => {
      setModalContent(res?.msg)
      await addLog(user.id, id, 'Selesai mengerjakan test')

      if (onTestEnded) onTestEnded()
      removeLocalStorageData('question')
      removeLocalStorageData('camera_capture_log')
      // Sesi sukses selesai, isSubmittingRef tetap true agar tidak tertembak ulang
    },
    onError: () => {
      setModalContent('Gagal mengirim jawaban. Silahkan periksa koneksi internet Anda.')
      isSubmittingRef.current = false // Buka kunci supaya user bisa retry
    },
  })

  const handleRetry = () => {
    isSubmittingRef.current = false
    const currentValues = form.getValues()
    serializePayload(currentValues)
  }

  // START: trigger camera new mechanism
  useEffect(() => {
    // if (rules?.camera === false) return
    if (rules?.camera !== 1) return

    const raw = localStorage.getItem('question')
    if (!raw) return

    const data = JSON.parse(raw)
    const camera = data.camera
    if (!camera) return

    const questionNumber = current + 1

    if (rules?.camera === 1 && shouldCaptureCamera(camera, questionNumber)) {
      console.log('CAPTURE TRIGGERED AT', questionNumber)

      // alert('CAPTURE TRIGGERED AT', questionNumber)

      captureOnce?.(questionNumber)

      const updatedCamera = {
        ...camera,
        captured: [...camera.captured, questionNumber],
      }

      const updatedData = {
        ...data,
        camera: updatedCamera,
      }

      localStorage.setItem('question', JSON.stringify(updatedData))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, modules?.rules])
  // END: trigger camera new mechanism

  useEffect(() => {
    if (rules?.geo !== 0) {
      getCurrentPosition({ timeout: 15000 })
        .then((loc) => {
          localStorage.setItem('geoLoc', JSON.stringify(loc))
        })
        .catch(() => {})
    }
  }, [rules?.geo])

  const next = () => {
    if (current === maxCount) return
    setCurrent((prev) => prev + 1)
    dispatch({
      type: ADD_CURRENT_QUESTION,
    })
    saveQuestionLocalStorage(form.getValues(), user.id)
  }

  useEffect(() => {
    dispatch({
      type: SET_CURRENT_QUESTION,
      currentPage: current + 1,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 1. Simpan progress ke localStorage setiap detik
  useEffect(() => {
    saveQuestionLocalStorage(form.getValues(), user.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minutes, seconds])

  // 2. Submit otomatis hanya ketika waktu habis (isStop)
  useEffect(() => {
    if (!isStop || isSubmittingRef.current) return
    form.handleSubmit(create)()
    if (onTestEnded) onTestEnded()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStop])

  const handleLastCameraCapture = async (questionNumber) => {
    const raw = localStorage.getItem('question')
    if (!raw) return

    const parsed = JSON.parse(raw)
    const camera = parsed.camera
    if (!camera) return

    if (shouldCaptureCamera(camera, questionNumber)) {
      await captureOnce?.(questionNumber)

      const updated = {
        ...parsed,
        camera: {
          ...camera,
          captured: [...camera.captured, questionNumber],
        },
      }

      localStorage.setItem('question', JSON.stringify(updated))
      window._lastForcedCapture = { [questionNumber]: 'success' }
    }
  }

  async function create(formField) {
    const isLastQuestion = current === maxCount

    // Jika bukan soal terakhir dan waktu belum habis, lanjut ke nomor berikutnya
    if (!isLastQuestion && !isStop) {
      next()
      return
    }

    // Guard eksekusi final submit (hanya boleh dieksekusi 1 kali)
    if (isSubmittingRef.current) return
    isSubmittingRef.current = true

    if (isLastQuestion && rules?.camera === 1) {
      await handleLastCameraCapture(current + 1)
    }

    setIsOpen(true)
    serializePayload(formField)
  }

  function saveQuestionLocalStorage(question, userId) {
    const existing = localStorage.getItem('question')
    let existingData = existing ? JSON.parse(existing) : null

    // Cek apakah data lama masih relevan dengan modul & question sekarang
    const isSameModule =
      existingData &&
      Number(existingData.moduleID) === Number(modules.id) &&
      Number(existingData.questionID) === Number(dataQuestion.id)

    // Kalau beda modul / question → reset time_start baru
    if (!isSameModule) {
      existingData = { time_start: new Date().toISOString() }
    } else if (!existingData.time_start) {
      // fallback jaga-jaga kalau field-nya hilang
      existingData.time_start = new Date().toISOString()
    }

    // INIT CAMERA SEKALI SAJA
    let camera = existingData?.camera
    if (!camera && rules?.camera !== 0) {
      camera = {
        layout: 9,
        targets: buildCameraTargets(totalQuestion, 9),
        captured: [],
      }
    }

    const dataStorage = {
      ...existingData,
      userId,
      moduleID: Number(modules.id),
      questionID: Number(dataQuestion.id),
      minutes,
      seconds,
      currentPage: current,
      questionPage: current + 1,
      questions: { ...question },
      camera,
    }

    localStorage.setItem('question', JSON.stringify(dataStorage))
  }

  function sendAnswer(newPayload, payload) {
    if (modules.nama_modul === 'Career Direction Profile') {
      const jsonPoint = {
        leteral: 0,
        enrichment: 0,
        vertical: 0,
        reagliment: 0,
        relocation: 0,
      }
      const jsonPointString = {}
      const key = Object.keys(payload)
      const objKey = Object.keys(jsonPoint)
      const total = Math.round(key.length / 5)
      for (let i = 0; i < total; i++) {
        for (let j = 0; j < 5; j++) {
          jsonPoint[objKey[j]] += payload[key[i * 5 + j]]
        }
      }
      for (let k in jsonPoint) {
        jsonPointString[k] = `${jsonPoint[k]}`
      }
      const rawData = dataQuestion.questionsList
      const keysPayload = Object.keys(payload)
      const answerCount = rawData.filter(
        (f, i) => f.kunci_jawaban === payload[keysPayload[i]],
      ).length

      const answerPayload = {
        id_user: user.id,
        id_modul: dataQuestion.id,
        jawaban: answerCount,
        tipe_modul: dataQuestion.moduleType,
        json_jawaban: JSON.stringify({ answer: newPayload }),
        json_nilai: JSON.stringify(jsonPointString),
      }
      mutate(answerPayload)
    } else if (modules.tipe_modul === 'PILIH SALAH SATU') {
      const answerPayload = {
        id_user: user.id,
        id_modul: dataQuestion.id,
        tipe_modul: dataQuestion.moduleType,
        json_jawaban: JSON.stringify({ answer: payload }),
      }

      mutate(answerPayload)
    } else {
      const rawData = dataQuestion.questionsList
      const keysPayload = Object.keys(payload)
      const answerCount = rawData.filter(
        (f, i) => f.kunci_jawaban === payload[keysPayload[i]],
      ).length

      const answerPayload = {
        id_user: user.id,
        id_modul: dataQuestion.id,
        jawaban: answerCount,
        tipe_modul: dataQuestion.moduleType,
        json_jawaban: JSON.stringify({ answer: newPayload }),
      }

      mutate(answerPayload)
    }
  }

  function serializePayload(payload) {
    isSubmittingRef.current = true

    try {
      const newPayload = {}
      const keys =
        dataQuestion.moduleType === 'ESAI LENGTH'
          ? Object.keys(payload).reverse()
          : Object.keys(payload)

      for (let i = 0; i < keys.length; i++) {
        newPayload[i + 1] = `${payload[keys[i]]}`
      }

      sendAnswer(newPayload, payload)
    } catch (e) {
      isSubmittingRef.current = false
      throw e
    }
  }

  function handleCloseModal() {
    if (!isSuccess) return
    setIsOpen(false)
    removeLocalStorageData('module')
    removeLocalStorageData('question')
    removeLocalStorageData('camera_capture_log')
    removeLocalStorageData('geoLoc')
    window.location.href = (process.env.REACT_APP_BASE_NAME || '') + '/'
  }

  return (
    <Box>
      <QuestionHeader minutes={minutes} seconds={seconds} />
      <form onSubmit={form.handleSubmit(create)}>
        {React.cloneElement(React.Children.toArray(children).at(current), { form })}
        <Box sx={{ float: 'right', mt: 3 }}>
          <Button
            ref={btnSubmitRef}
            variant="contained"
            type="submit"
            disabled={isLoading || isSubmittingRef.current}
          >
            {isLoading || isSubmittingRef.current ? 'Sending...' : 'Submit'}
          </Button>
        </Box>
      </form>

      {/* MODAL MODIFIKASI */}
      <Modal
        open={isOpen}
        title={isLoading ? 'Mengirim Jawaban...' : isError ? 'Pengiriman Gagal' : 'Selesai!'}
        handleClose={isSuccess ? handleCloseModal : undefined}
        hideFooter={true}
        disableBackdropClick={isSuccess}
        disableEscapeKeyDown={isSuccess}
      >
        <Box sx={{ textAlign: 'center', p: 2 }}>
          {isLoading && (
            <Box sx={{ mb: 2 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>
                Sedang memproses data, mohon jangan tutup halaman ini...
              </Typography>
            </Box>
          )}

          {isError && (
            <Box>
              <Typography color="error">{modalContent}</Typography>
              <Button variant="outlined" onClick={handleRetry} sx={{ mt: 2 }}>
                Kirim Ulang Jawaban
              </Button>
            </Box>
          )}

          {isSuccess && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {modalContent || 'Berhasil!'}.
              </Typography>
              <Typography>Data Anda telah aman tersimpan di server kami.</Typography>
              <Typography sx={{ fontWeight: 'bold', mt: 1 }}>
                Silahkan klik beranda untuk kembali ke halaman utama
              </Typography>
              <Button variant="contained" onClick={handleCloseModal} sx={{ mt: 3 }}>
                Kembali ke Beranda
              </Button>
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  )
})

Wizard.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Wizard
